<template>
  <div v-if="distribution" class="distribution-detail-container h-100">
    <b-row class="heading-container">
      <go-back />
      <h4 class="ml-2">
        {{ distribution.id }} -
        {{ distribution.description }}
      </h4>
    </b-row>
    <tabs-layout :tabs="sections" :lazy-btabs="false" @change="handleTabChange" style="flex-grow: 1;">
      <template v-slot:tabTitle="{ tab }">
        {{ tab.title }}
      </template>
      <template v-slot:content="{ tab }">
        <component
          :ref="tab.ref"
          :is="tab.component"
          :distribution="distribution"
          :has-transactions="hasTransactions()"
          :transactions-running="transactionsRunning()"
          :editMode="editMode"
          :connectTariffPeriod="connectTariffPeriod()"
          :internationalTariffPeriod="internationalTariffPeriod()"
          :standardTariffPeriod="standardTariffPeriod()"
          class="p-3"
          v-bind="tab.extra"
          @validated="e => sectionValidated(tab, e)"
        />
      </template>
    </tabs-layout>
    <b-row class="fixed-footer">
      <b-col class="d-flex justify-content-end" style="padding: 0">
        <b-button v-if="editMode" class="btn-outline-dark" @click="cancel">{{ $t("cancel") | capitalize }}</b-button>
        <b-button
          class="ml-3 btn-outline-red"
          :disabled="!canSubmit || updatedFields().length === 0"
          @click="submit"
          v-if="editMode"
        >
          {{ $t("save") | capitalize }}
        </b-button>
        <b-button
          class="btn-outline-red"
          @click="edit"
          v-if="!editMode && this.permissions.pages[Pages.DistributionList].canWrite && tabEditPermission"
        >
          {{ $t("edit") | capitalize }}
        </b-button>
      </b-col>
    </b-row>
    <modal
      id="cancel-modal"
      :cancel-title="$t('no_continue_editing') | capitalize"
      :ok-title="$t('yes_cancel_editing') | capitalize"
      @ok="realCancel()"
      @cancel="$bvModal.hide('cancel-modal')"
      modal-type="info"
      centered
      :custom-title="$t('edit_tariff_period') | capitalize"
    >
      {{ $t("cancel_edit_tariff_period") }}
    </modal>
    <modal
      id="confirm-modal"
      :cancel-title="$t('cancel') | capitalize"
      :ok-title="$t('confirm') | capitalize"
      @ok="realSubmit()"
      @cancel="$bvModal.hide('confirm-modal')"
      modal-type="info"
      size="lg"
      centered
      :custom-title="$t('edit_tariff_period') | capitalize"
      >
      <p>
        {{ $t("edit_tariff_period_confirm") }}
      </p>
      <b-row v-for="(pair, index) in updatedFieldsForDisplay()" :key="index">
        <b-col cols="6" v-for="field in pair" :key="field[0]">
          {{ fieldLabel(field[0]) }}: {{ field[1] }}
        </b-col>

      </b-row>
    </modal>
  </div>
</template>
<script>

import { camelCase, capitalize, chunk, isArray, isEqual } from "lodash"
import { canadaCurrencyCode, distributionMethodologies, distributionReserveValueTypes } from "@/constants"
import { mapActions, mapState } from "vuex"
import EligibilitySection from "@/pages/Distributions/DetailSections/EligibilitySection"
import MainSection from "@/pages/Distributions/DetailSections/MainSection"
import OpenPeriodSection from "@/pages/Distributions/DetailSections/OpenPeriodSection"
import OtherSection from "@/pages/Distributions/DetailSections/OtherSection"
import { Pages } from "@/utils/pages"
import TransactionsList from "@/pages/Transactions/TransactionsList"
import VPPSection from "@/pages/Distributions/DetailSections/VPPSection"
import moment from "moment"

export default {
  name: "DistributionDetails",
  props: {
    distributionId: String
  },
  data () {
    return {
      Pages,
      distribution: null,
      originalDistribution: null,
      editMode: false,
      tabEditPermission: true,
      vppSection: {
        ref: "vpp",
        title: this.$t("vpp"),
        component: VPPSection,
        fields: {},
        valid: true
      },
    }
  },
  methods: {
    ...mapActions("alert", ["success", "error"]),
    standardTariffPeriod () {
      return this.distribution.distributionMethodology === distributionMethodologies.STANDARD
    },
    internationalTariffPeriod () {
      return this.distribution.distributionMethodology === distributionMethodologies.INTERNATIONAL
    },
    connectTariffPeriod () {
      return [
        distributionMethodologies.CONNECT_OPEN,
        distributionMethodologies.CONNECT_ONE_TIME,
        distributionMethodologies.CONNECT_TRACK_SPECIFIC
      ].includes(this.distribution.distributionMethodology)
    },
    showVPP () {
      return ![
        distributionMethodologies.CONNECT_TRACK_SPECIFIC,
        distributionMethodologies.INTERNATIONAL
      ].includes(this.distribution.distributionMethodology)
    },
    async getData () {
      let response = await this.$api.distributions.getDistribution((this.distributionId))
      this.copyFormatDistribution(response.data)
    },
    edit () {
      this.editMode = true
    },
    cancel () {
      this.$bvModal.show("cancel-modal")
    },
    async realCancel () {
      this.editMode = false
      await this.getData()
      this.success(this.$t("tariff_period_edit_cancelled"))
    },
    unflatten (obj) {
      const result = {}
      for (let i in obj) {
        const keys = i.split(".")
        keys.reduce(function (r, e, j) {
          return r[e] || (r[e] = (keys.length - 1 === j ? obj[i] : {}))
        }, result)
      }
      return result
    },
    sectionValidated (section, { valid, fields }) {
      section.valid = valid
      section.fields = fields
    },
    getNestedObjectUpdatedFields (updatedObj, originalObj, prefix) {
      const updatedFields = []
      Object.entries(updatedObj).forEach(([k, v]) => {
        if (typeof v === "object" && !Array.isArray(v) && v !== null) {
          updatedFields.push(...this.getNestedObjectUpdatedFields(v, originalObj[k], (prefix ?? "") + k))
        } else {
          if (!isEqual(originalObj[k], v)) {
            updatedFields.push([(prefix ? prefix + "." : "") + k, v])
          }
        }
      })
      return updatedFields
    },
    updatedFields () {
      return this.getNestedObjectUpdatedFields(this.distribution, this.originalDistribution)
    },
    updatedFieldsForDisplay () {
      let fields = this.updatedFields()
      fields = fields.map(([k, v]) => [k, ["periodStart", "periodEnd", "openPeriodEndMaker", "openPeriodEndPerformer"].includes(k) ? moment(v).format(this.$config.DATE_FORMAT) : v])
      fields = fields.map(([k, v]) => [k, isArray(v) ? v.join(", ") : v])
      let fieldsObj = Object.fromEntries(fields)
      let fieldNames = new Set(fields.map(([k,]) => k))
      if (fieldNames.has("reserveValueTypePerformer") || fieldNames.has("revenueReservePerformer")) {
        fieldsObj.revenueReservePerformer = this.addValueType(this.distribution.revenueReservePerformer, this.distribution.reserveValueTypePerformer)
        delete fieldsObj.reserveValueTypePerformer
      }
      if (fieldNames.has("reserveValueTypeMaker") || fieldNames.has("revenueReserveMaker")) {
        fieldsObj.revenueReserveMaker = this.addValueType(this.distribution.revenueReserveMaker, this.distribution.reserveValueTypeMaker)
        delete fieldsObj.reserveValueTypeMaker
      }
      if (fieldNames.has("withholdingTaxValueType") || fieldNames.has("withholdingTax")) {
        fieldNames.withholdingTax = this.addValueType(this.distribution.withholdingTax, this.distribution.withholdingTaxValueType)
        delete fieldsObj.withholdingTaxValueType
      }
      if (fieldNames.has("moratoriumMakerMinimumType") || fieldNames.has("moratoriumMakerMinimum")) {
        fieldNames.moratoriumMakerMinimum = this.addValueType(this.distribution.moratoriumMakerMinimum, this.distribution.moratoriumMakerMinimumType)
        delete fieldsObj.moratoriumMakerMinimumType
      }
      if (fieldNames.has("moratoriumPerformerMinimumType") || fieldNames.has("moratoriumPerformerMinimum")) {
        fieldNames.moratoriumPerformerMinimum = this.addValueType(this.distribution.moratoriumPerformerMinimum, this.distribution.moratoriumPerformerMinimumType)
        delete fieldsObj.moratoriumPerformerMinimumType
      }
      return chunk(Object.entries(fieldsObj), 2)
    },
    fieldLabel (field) {
      field = camelCase(field)
      for (let section of this.sections) {
        if (typeof section.fields[field] !== "undefined") {
          return section.fields[field]
        }
      }
      return field
    },
    addValueType (value, valueType) {
      if (valueType === distributionReserveValueTypes.PERCENT) {
        return `${value}%`
      } else {
        return `${canadaCurrencyCode} ${value}`
      }
    },
    copyFormatDistribution (data) {
      this.distribution = data
      // Format numbers
      Object.keys(this.distribution).forEach(k => {
        if (!isNaN(parseFloat(this.distribution[k])) && !isNaN(this.distribution[k] - 0)) {
          this.distribution[k] = Number(this.distribution[k])
        }
      })
      this.originalDistribution = JSON.parse(JSON.stringify(this.distribution))
    },
    submit () {
      this.$bvModal.show("confirm-modal")
    },
    async realSubmit () {
      try {
        const data = this.unflatten(Object.fromEntries(this.updatedFields().map(([k, v]) => [k, v === "" ? null : v])))
        if (data.vpp) {
          data.vpp.addedBy = this.user.uuid
          data.vpp.addedAt = moment(new Date()).format(this.$config.ISO_DATETIME_FORMAT)
          if (data.vpp.calculateAutomatically) {
            data.vpp.performer = null
            data.vpp.maker = null
          } else if (data.vpp.calculateAutomatically === false) {
            data.playcounts_ineligible_percentage = null
            data.playcounts_unmatched_percentage = null
          }
        }
        let response = await this.$api.distributions.updateDistribution(this.distributionId, data)
        this.copyFormatDistribution(response.data)
        this.success(capitalize(this.$t("saved_changes")))
        this.editMode = false
      } catch (e) {
        let message = ""
        if (e.response.status === 422) {
          message = "Failed to update distribution. Please correct the data and try again."
          if (e.response.data.fieldErrors) {
            for (let section of this.sections.filter(s => s.ref !== undefined && s.ref !== null)) {
              this.$refs[section.ref].setErrors(e.response.data.fieldErrors)
            }
          }
          if (e.response.data.nonFieldErrors) {
            message = message + "\n" + e.response.data.nonFieldErrors.map(e => e.message).join("\n")
          }
        } else {
          message = "Failed to update distribution. Please contact administrator or try again."
        }
        this.error(message)
      }
    },
    hasTransactions () {
      return this.distribution.transactionInfo?.calculations?.filter(c => c.status === "success").length > 0
    },
    transactionsRunning () {
      return !!this.distribution.transactionInfo?.transactionIsRunning
    },
    updateDistributionValue (value) {
      const transactionTab = this.sections.find(tab => tab.ref === "transactions")
      if (transactionTab) {
        transactionTab.extra.distributionValue = value
      }
    },
    handleTabChange ({ tab }) {
      this.tabEditPermission = !tab.preventEdit
    }
  },
  computed: {
    ...mapState("user", ["permissions", "user"]),
    canSubmit () {
      return this.sections.reduce((o, t) => o && t.valid, true)
    },
    sections () {
      const sections = [
        {
          ref: "main",
          title: capitalize(this.$t("main_details")),
          component: MainSection,
          fields: {},
          valid: true
        },
        {
          ref: "open_period",
          title: capitalize(this.$t("open_period")),
          component: OpenPeriodSection,
          fields: {},
          valid: true
        },
        {
          ref: "eligibility",
          title: capitalize(this.$t("distribution_methodology_and_eligibility")),
          component: EligibilitySection,
          fields: {},
          valid: true
        },
        {
          ref: "other",
          title: capitalize(this.$t("other")),
          component: OtherSection,
          fields: {},
          valid: true
        },
        {
          ref: "transactions",
          title: capitalize(this.$t("transactions")),
          component: TransactionsList,
          valid: true,
          fields: {},
          preventEdit: true,
          extra: {
            isSubTab: true,
            distributionId: this.distributionId,
            distributionValue: "",
          },
        }
      ]
      if (this.showVPP()) {
        sections.splice(3, 0, this.vppSection)
      }
      return sections
    }
  },
  watch: {
    "distribution" (distribution) {
      if (distribution.description) {
        this.updateDistributionValue(distribution.description)
      }
    }
  },
  mounted () {
    this.getData()
  }
}
</script>
<style lang="scss" scoped>
.distribution-detail-container {
  display: flex;
  flex-direction: column;
  .heading-container {
    flex-grow: 0;
    min-height: 60px;
  }
  ::v-deep .card-container {
    height: 100%;
    .tabs {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
    }
    .tab-content {
      height: 100%;
      margin-top: 15px;
      background-color: #fafafa !important;
      border-radius: 4px;
    }
  }
}

.fixed-footer {
    position: fixed;
    bottom: 40px;
    right: 0;
    margin-right: 60px;
    width: 100%;
}
</style>
